import CheckIcon from "@mui/icons-material/Check";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import { Box, Paper, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ResponsiveIframe from "components/ResponsiveIframe";
import { format } from "date-fns";
import { makeStyles } from "helpers/StyleHelper";
import { FormattedMessage as T, useIntl } from "react-intl";
import { QuestionTypeEnum } from "types/common-types";

import {
  canHaveConfidenceLevels,
  isMultipleChoiceOrAnswersQuestionType,
} from "helpers/QuestionTypeHelpers";
import {
  IEvaluationQuestionAnswer,
  Question,
} from "services/KnowfullyAdminClient";

interface IAnswerWithId extends IEvaluationQuestionAnswer {
  id: string;
}

const useStyles = makeStyles({
  answer: {
    flexGrow: 1,
  },
  elementContainer: {
    display: "flex",
    marginTop: "10px",
    "& b": {
      marginRight: "10px",
    },
  },
});

type QuestionAndAnswersProps = {
  question?: Question | null;
};

const QuestionAndAnswers = ({ question }: QuestionAndAnswersProps) => {
  const styles = useStyles(useTheme());
  const { formatMessage: t } = useIntl();

  if (!question) return null;

  const answers = ((question!.evaluationQuestionAnswers ??
    []) as IAnswerWithId[]).sort(
    (a: IAnswerWithId, b: IAnswerWithId) =>
      (a.sequence ?? 0) - (b.sequence ?? 0)
  );

  const isMultipleChoice =
    question.questionType?.value &&
    isMultipleChoiceOrAnswersQuestionType(question.questionType.value);

  return (
    <>
      <Typography component="div">
        <ResponsiveIframe
          srcDoc={question.value}
          title="questionHtml"
          forceTableBorders
        />
      </Typography>
      <Box my={3}>
        {question!.createdOn && (
          <Typography sx={styles.elementContainer}>
            <b>
              <T id="questionsCreatedOnLabel" />:
            </b>
            {format(question!.createdOn, "P")}
          </Typography>
        )}
        {question!.questionType && (
          <Typography sx={styles.elementContainer}>
            <b>
              <T id="questionsTypeLabel" />:
            </b>
            {question!.questionType.name}
          </Typography>
        )}
        <Typography sx={styles.elementContainer}>
          <b>
            <T id="questionsRequiredLabel" />:
          </b>
          {question!.required ? <CheckIcon /> : <CloseIcon />}
        </Typography>
        {canHaveConfidenceLevels(question.questionType) && (
          <Typography sx={styles.elementContainer}>
            <b>
              <T id="addConfidenceLevelsLabel" />:
            </b>
            {question!.hasConfidenceLevels ? <CheckIcon /> : <CloseIcon />}
          </Typography>
        )}
        {question!.ratingSetting && (
          <Typography sx={styles.elementContainer}>
            <b>
              <T id="questionsRatingSettingLabel" />:
            </b>
            {question!.ratingSetting.name}
          </Typography>
        )}
        {question!.questionType?.value === QuestionTypeEnum.TextQuestion &&
          !!answers.length && (
            <Typography sx={styles.elementContainer}>
              <b>
                <T id="expectedAnswerLabel" />:
              </b>
            </Typography>
          )}
      </Box>

      {isMultipleChoice &&
        answers.map((a) => (
          <Box key={a.id} mb={2}>
            <Paper key={a.id} elevation={3} square>
              <Box p={1} display="flex" alignItems="center">
                <Box sx={styles.answer}>
                  <ResponsiveIframe
                    srcDoc={a.value!}
                    title="answerHtml"
                    forceTableBorders
                  />
                </Box>

                {a.isAnswer && (
                  <Box ml={2} display="flex" alignItems="center">
                    <CheckCircleIcon
                      titleAccess={t({ id: "questionFormAnswerTitle" })}
                    />
                  </Box>
                )}
              </Box>
            </Paper>
          </Box>
        ))}

      {question.questionType?.value === QuestionTypeEnum.TextQuestion &&
        !!answers.length && (
          <Box mb={2}>
            <Paper elevation={3} square>
              <Box p={1} display="flex" alignItems="center">
                <ResponsiveIframe
                  srcDoc={answers.length ? answers[0].value! : ""}
                  title="answerHtml"
                  forceTableBorders
                />
              </Box>
            </Paper>
          </Box>
        )}

      {question.questionFeedbackId && (
        <Box mb={2}>
          <Typography sx={styles.elementContainer}>
            <b>
              <T id="questionFeedbackLabel" />:
            </b>
          </Typography>
          <ResponsiveIframe
            srcDoc={question.questionFeedback ?? ""}
            title="questionsFeedbackLabel"
            forceTableBorders
          />
        </Box>
      )}
    </>
  );
};

export default QuestionAndAnswers;
